// Enhanced 3D Model Gallery - Professional Controls
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import { KTX2Loader } from 'three/examples/jsm/loaders/KTX2Loader.js';
import { HDRLoader } from 'three/examples/jsm/loaders/HDRLoader.js';

// Global variables
let scene, camera, renderer, controls;
let loadedModels = new Map();
let selectedModel = null;
let animationMixers = new Map();
let timer = new THREE.Timer();
let stats = { fps: 0, triangles: 0, drawCalls: 0, memory: 0 };
let frameCount = 0;
let lastTime = performance.now();
let primaryShadowLight = null;
let currentEnvironmentType = 'neutral';
let lastEnvironmentBackground = null;
let gltfLoader = null;
let dracoLoader = null;
let ktx2Loader = null;
let officeMonitorVideoElement = null;
let officeMonitorVideoTexture = null;
let officeMonitorVideoMaterial = null;
const DEFAULT_EXPOSURE = 1.0;
const DEFAULT_TONE_MAPPING = 'Linear';
const FORCE_ORIGINAL_MODEL_FILES = new Set(['bookanim.glb']);
const DEFAULT_OUTLINE_COLOR = '#000000';
const DEFAULT_OUTLINE_THICKNESS = 0.003;
const OFFICE_VIDEO_MODEL_ID = 'officespace';
const OFFICE_VIDEO_MATERIAL_NAME = 'Monitor';
const OFFICE_VIDEO_PATH = '/vid01.mp4';
let lightingApplyToken = 0;
const STEAM_TARGET_MODEL_ID = 'officespace';
const STEAM_EMITTER_NAME = 'steam_emitter';
const STEAM_TEXTURE_PATH = './textures/puff.png';
let steamTexture = null;

// Model configuration
let modelConfig = null;

// Initialize the application
async function init() {
    console.log('🎯 Initializing Enhanced 3D Model Gallery...');
    
    // Load model configuration
    await loadModelConfiguration();
    
    // Setup Three.js scene
    setupScene();
    setupCamera();
    setupRenderer();
    setupModelLoaders();
    setupControls();
    setupLighting();
    setupEnvironment();
    
    // Setup UI
    setupUI();
    populateModelList();
    
    // Auto-load first model
    await autoLoadFirstModel();
    
    // Setup WebXR
    await initWebXR();
    
    // Start animation loop
    renderer.setAnimationLoop(animate);
    
    console.log('✅ Gallery initialized successfully!');
}

// Load model configuration from JSON
async function loadModelConfiguration() {
    try {
        const response = await fetch('./models/models.json');
        modelConfig = await response.json();
        console.log('📋 Model configuration loaded:', modelConfig.models.length, 'models');
    } catch (error) {
        console.error('❌ Failed to load model configuration:', error);
        // Fallback configuration
        modelConfig = {
            models: [],
            settings: {
                defaultEnvironment: 'neutral',
                autoRotate: false,
                showStats: true,
                enableShadows: false,
                antialias: true
            }
        };
    }
}

// Scene setup
function setupScene() {
    scene = new THREE.Scene();
    scene.background = new THREE.Color(0x2e2e2e);
    lastEnvironmentBackground = scene.background;
    
    // Add fog for atmospheric ground plane blending
    scene.fog = new THREE.Fog(0x2a2a3e, 8, 25); // Start much closer to camera
}

// Camera setup
function setupCamera() {
    camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
    camera.position.set(2, 2, 2);
}

// Renderer setup
function setupRenderer() {
    const canvas = document.getElementById('three-canvas');
    renderer = new THREE.WebGLRenderer({ 
        canvas: canvas,
        antialias: modelConfig?.settings?.antialias ?? true 
    });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.shadowMap.enabled = modelConfig?.settings?.enableShadows ?? true;
    renderer.shadowMap.type = THREE.PCFShadowMap;
    renderer.xr.enabled = true;
    renderer.toneMapping = THREE.LinearToneMapping;
    renderer.toneMappingExposure = DEFAULT_EXPOSURE;
}

function setExposure(value) {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return;
    renderer.toneMappingExposure = numericValue;
    const exposureSlider = document.getElementById('exposure-slider');
    const exposureValue = document.getElementById('exposure-value');
    if (exposureSlider) exposureSlider.value = String(numericValue);
    if (exposureValue) exposureValue.textContent = numericValue.toFixed(1);
}

function setToneMapping(toneMappingName) {
    const mappingName = String(toneMappingName || DEFAULT_TONE_MAPPING);
    if (mappingName === 'ACESFilmic') {
        renderer.toneMapping = THREE.ACESFilmicToneMapping;
    } else {
        renderer.toneMapping = THREE.LinearToneMapping;
    }

    const toneMappingSelect = document.getElementById('tone-mapping');
    if (toneMappingSelect && toneMappingSelect.value !== mappingName) {
        toneMappingSelect.value = mappingName;
    }
}

function resolveModelLightingDefaults(modelData) {
    const modelLighting = modelData?.lighting || {};
    const defaultEnvironment = modelConfig?.settings?.defaultEnvironment ?? 'neutral';
    const requestedEnvironment = typeof modelLighting.environment === 'string' ? modelLighting.environment : defaultEnvironment;
    const environment = ['studio', 'outdoor', 'dark', 'neutral', 'nature'].includes(requestedEnvironment)
        ? requestedEnvironment
        : defaultEnvironment;
    const requestedToneMapping = typeof modelLighting.toneMapping === 'string' ? modelLighting.toneMapping : DEFAULT_TONE_MAPPING;
    const toneMapping = requestedToneMapping === 'ACESFilmic' ? 'ACESFilmic' : 'Linear';
    const exposureValue = Number(modelLighting.exposure);
    const exposure = Number.isFinite(exposureValue) ? exposureValue : DEFAULT_EXPOSURE;

    return {
        environment,
        toneMapping,
        exposure
    };
}

async function applyModelLightingDefaults(modelData) {
    const token = ++lightingApplyToken;
    const { environment, toneMapping, exposure } = resolveModelLightingDefaults(modelData);
    const environmentSelect = document.getElementById('environment-select');

    if (environmentSelect && environmentSelect.value !== environment) {
        environmentSelect.value = environment;
    }
    await changeEnvironment(environment);
    if (token !== lightingApplyToken) return;

    setToneMapping(toneMapping);
    setExposure(exposure);
}

function setupModelLoaders() {
    dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath('/decoders/draco/gltf/');

    ktx2Loader = new KTX2Loader();
    ktx2Loader.setTranscoderPath('/decoders/basis/');
    ktx2Loader.detectSupport(renderer);

    gltfLoader = new GLTFLoader();
    gltfLoader.setDRACOLoader(dracoLoader);
    gltfLoader.setKTX2Loader(ktx2Loader);
}

// Controls setup
function setupControls() {
    controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.minDistance = 1.5;
    controls.maxDistance = 10;
    controls.autoRotate = modelConfig?.settings?.autoRotate ?? true;
    controls.autoRotateSpeed = 2.0;
}

// Shadow helper utilities
function configureShadowDirectional(light, {
    mapSize = 4096,
    near = 0.1,
    far = 40,
    frustum = 7,
    bias = -0.0005,
    normalBias = 0.02
} = {}) {
    light.castShadow = true;
    light.shadow.mapSize.set(mapSize, mapSize);
    light.shadow.bias = bias;
    light.shadow.normalBias = normalBias;
    const c = light.shadow.camera;
    c.near = near;
    c.far = far;
    c.left = -frustum;
    c.right = frustum;
    c.top = frustum;
    c.bottom = -frustum;
    c.updateProjectionMatrix();
}

function configureShadowSpot(light, {
    mapSize = 2048,
    near = 1,
    far = 40,
    bias = -0.0001,
    normalBias = 0.03
} = {}) {
    light.castShadow = true;
    light.shadow.mapSize.set(mapSize, mapSize);
    light.shadow.bias = bias;
    light.shadow.normalBias = normalBias;
    const c = light.shadow.camera;
    c.near = near;
    c.far = far;
    c.updateProjectionMatrix();
}

function fitDirLightToModel(light, model, padding = 1.5) {
    if (!light || !light.shadow || !model) return;
    const box = new THREE.Box3().setFromObject(model.scene);
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());

    // Aim light target at model center
    light.target.position.copy(center);
    light.target.updateMatrixWorld();
    if (!light.target.parent) scene.add(light.target);

    const radius = Math.max(size.x, size.y, size.z) * 0.5 * padding;
    const c = light.shadow.camera;
    c.left = -radius;
    c.right = radius;
    c.top = radius;
    c.bottom = -radius;
    c.updateProjectionMatrix();
}

// Lighting setup
function setupLighting() {
    // Remove existing lights
    const lightsToRemove = [];
    scene.traverse((child) => {
        if (child.isLight) {
            lightsToRemove.push(child);
        }
    });
    lightsToRemove.forEach(light => scene.remove(light));
    setupNeutralLighting();
}

// Environment setup
function setupEnvironment() {
    // Load ground texture
    const textureLoader = new THREE.TextureLoader();
    const groundTexture = textureLoader.load('./textures/grid08.png');
    
    // Configure texture for subtle repeating pattern
    groundTexture.wrapS = THREE.RepeatWrapping;
    groundTexture.wrapT = THREE.RepeatWrapping;
    groundTexture.repeat.set(20, 20); // Repeat 20x20 times for subtle pattern
    groundTexture.magFilter = THREE.LinearFilter;
    groundTexture.minFilter = THREE.LinearMipmapLinearFilter;
    
    // Ground plane with textured material
    const planeGeometry = new THREE.PlaneGeometry(50, 50);
    const planeMaterial = new THREE.MeshStandardMaterial({ 
        map: groundTexture,
        color: 0x888888, // Slightly darker for subtlety
        roughness: 0.8,
        metalness: 0.1
    });
    const plane = new THREE.Mesh(planeGeometry, planeMaterial);
    plane.rotation.x = -Math.PI / 2;
    plane.position.y = 0;
    plane.receiveShadow = true;
    plane.name = 'ground-plane';
    // scene.add(plane);
}

// Auto-load first model
async function autoLoadFirstModel() {
    if (modelConfig && modelConfig.models && modelConfig.models.length > 0) {
        const firstModel = modelConfig.models[0];
        console.log(`🚀 Auto-loading first model: ${firstModel.name}`);
        try {
            await loadModel(firstModel);
        } catch (error) {
            console.error(`❌ Failed to auto-load first model: ${firstModel.name}`, error);
        }
    }
}

// Populate model list in sidebar
function populateModelList() {
    const modelList = document.getElementById('model-list');
    modelList.innerHTML = '';

    if (!modelConfig || !modelConfig.models) {
        modelList.innerHTML = '<div class="model-item"><div class="model-info"><div class="model-name">No models configured</div></div></div>';
        return;
    }

    modelConfig.models.forEach(model => {
        const modelItem = document.createElement('div');
        modelItem.className = 'model-item';
        modelItem.dataset.modelId = model.id;
        
        // Create thumbnail element - support both images and text/emoji
        let thumbnailContent;
        if (model.thumbnail.startsWith('./') || model.thumbnail.startsWith('http')) {
            // Image thumbnail
            thumbnailContent = `<img src="${model.thumbnail}" alt="${model.name}" class="thumbnail-image" onerror="this.style.display='none'; this.nextElementSibling.style.display='block';">
                               <div class="thumbnail-fallback" style="display:none;">📦</div>`;
        } else {
            // Text/emoji thumbnail (fallback)
            thumbnailContent = model.thumbnail;
        }
        
        modelItem.innerHTML = `
            <div class="model-thumbnail">${thumbnailContent}</div>
            <div class="model-info">
                <div class="model-name">${model.name}</div>
                <div class="model-type">${model.type}</div>
                <div class="model-description">${model.description}</div>
                <div class="model-status">Click to load</div>
            </div>
        `;
        
        modelItem.addEventListener('click', () => loadModel(model));
        modelList.appendChild(modelItem);
    });
}

// Load a specific model
async function loadModel(modelData) {
    const modelItem = document.querySelector(`[data-model-id="${modelData.id}"]`);
    const statusElement = modelItem.querySelector('.model-status');
    
    // Check if already loaded
    if (loadedModels.has(modelData.id)) {
        selectModel(modelData.id);
        return;
    }
    
    // Show loading state
    modelItem.classList.add('loading');
    statusElement.textContent = 'Loading...';
    showLoadingProgress(0, modelData.name);
    
    try {
        const modelFile = resolveModelFile(modelData);
        const modelPath = `./models/${modelFile}`;
        const [gltf, fileSizeBytes] = await Promise.all([
            new Promise((resolve, reject) => {
                gltfLoader.load(
                    modelPath,
                    resolve,
                    (progress) => {
                        showLoadingProgress(resolveLoadProgressPercent(progress), modelData.name);
                    },
                    reject
                );
            }),
            getModelFileSize(modelPath)
        ]);
        // Process the loaded model
        const processedModel = processLoadedModel(gltf, modelData, {
            filePath: modelPath,
            fileSizeBytes
        });
        loadedModels.set(modelData.id, processedModel);
        
        // Update UI
        modelItem.classList.remove('loading');
        statusElement.textContent = '✓ Loaded';
        hideLoadingProgress();
        
        // Select the newly loaded model
        selectModel(modelData.id);
        
        console.log(`✅ Model loaded: ${modelData.name}`);
        
    } catch (error) {
        console.error(`❌ Failed to load model ${modelData.name}:`, error);
        modelItem.classList.remove('loading');
        statusElement.textContent = '❌ Failed';
        hideLoadingProgress();
    }
}

async function getModelFileSize(modelPath) {
    try {
        const response = await fetch(modelPath, { method: 'HEAD' });
        const headerValue = response.headers.get('content-length');
        const fileSizeBytes = Number(headerValue);
        return Number.isFinite(fileSizeBytes) && fileSizeBytes >= 0 ? fileSizeBytes : null;
    } catch (error) {
        console.warn(`⚠️ Unable to determine file size for ${modelPath}:`, error);
        return null;
    }
}

function formatFileSize(bytes) {
    if (!Number.isFinite(bytes) || bytes < 0) return 'Unknown';

    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;

    while (size >= 1024 && unitIndex < units.length - 1) {
        size /= 1024;
        unitIndex += 1;
    }

    const decimals = unitIndex === 0 ? 0 : 1;
    return `${size.toFixed(decimals)} ${units[unitIndex]}`;
}

function resolveDisplayFileSize(fileSizeBytes, fallbackFileName) {
    if (Number.isFinite(fileSizeBytes) && fileSizeBytes >= 0) {
        return formatFileSize(fileSizeBytes);
    }

    return fallbackFileName ? `Unknown (${fallbackFileName})` : 'Unknown';
}

function resolveDisplayFileName(modelData, assetMeta = {}) {
    if (typeof assetMeta.filePath === 'string' && assetMeta.filePath.length > 0) {
        return assetMeta.filePath.split('/').pop();
    }

    const configuredFile = resolveModelFile(modelData);
    return typeof configuredFile === 'string' ? configuredFile.split('/').pop() : null;
}

function resolveLoadProgressPercent(progressEvent) {
    if (!progressEvent || !progressEvent.total) return 0;
    return (progressEvent.loaded / progressEvent.total) * 100;
}

function processLoadedModel(gltf, modelData, assetMeta = {}) {
    const fileName = resolveDisplayFileName(modelData, assetMeta);
    const fileSizeLabel = resolveDisplayFileSize(assetMeta.fileSizeBytes, fileName);
    const model = gltf.scene;
    const animations = gltf.animations;
    const box = new THREE.Box3().setFromObject(model);
    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());
    
    // Position model at origin (0,0,0)
    model.position.set(0, 0, 0);
    
    // Apply real-world scaling if configured
    applyRealWorldScale(model, modelData, size);
    
    // Enable shadows for all meshes
    let triangleCount = 0;
    let objectCount = 0;
    
    model.traverse((child) => {
        if (child.isMesh) {
            objectCount++;
            child.castShadow = true;
            child.receiveShadow = true;
            
            // Calculate triangle count
            if (child.geometry) {
                const geometry = child.geometry;
                if (geometry.index) {
                    triangleCount += geometry.index.count / 3;
                } else {
                    triangleCount += geometry.attributes.position.count / 3;
                }
            }
            
            // Ensure materials are properly configured
            if (child.material) {
                if (Array.isArray(child.material)) {
                    child.material.forEach(mat => {
                        mat.needsUpdate = true;
                    });
                } else {
                    child.material.needsUpdate = true;
                }
            }
        }
    });
    
    // Setup animations if available
    let mixer = null;
    let animationActions = [];
    if (animations && animations.length > 0) {
        mixer = new THREE.AnimationMixer(model);
        const loopMode = resolveAnimationLoopMode(modelData);
        const loopRepetitions = loopMode === THREE.LoopOnce ? 1 : Infinity;
        animationActions = animations.map(clip => {
            const action = mixer.clipAction(clip);
            action.setLoop(loopMode, loopRepetitions);
            action.clampWhenFinished = loopMode === THREE.LoopOnce;
            return {
                name: clip.name || `Animation ${animationActions.length + 1}`,
                action: action,
                clip: clip,
                duration: clip.duration
            };
        });
        animationMixers.set(modelData.id, mixer);
    }

    applyOfficeMonitorVideoMaterial(model, modelData);
    const steamSystem = setupModelSteamEffect(model, modelData);

    const outlineConfig = resolveOutlineConfig(modelData);
    const outlineMeshes = outlineConfig?.mode === 'invertedHull'
        ? applyInvertedHullOutline(model, outlineConfig)
        : [];
    
    // Add to scene
    scene.add(model);
    
    return {
        id: modelData.id,
        name: modelData.name,
        config: modelData,
        scene: model,
        originalGltf: gltf,
        animations: animationActions,
        mixer: mixer,
        boundingBox: box,
        size: size,
        center: center,
        triangles: Math.round(triangleCount),
        objects: objectCount,
        vertices: Math.round(triangleCount * 3), // Approximate
        faces: Math.round(triangleCount),
        fileSizeBytes: assetMeta.fileSizeBytes ?? null,
        fileSizeLabel,
        steamSystem: steamSystem,
        outlineMeshes: outlineMeshes,
        visible: true
    };
}

function resolveModelFile(modelData) {
    const originalFile = modelData.fileOriginal || modelData.file;
    const originalFileName = typeof originalFile === 'string'
        ? originalFile.split('/').pop().toLowerCase()
        : '';

    // Models listed here bypass optimization and always load original assets.
    if (FORCE_ORIGINAL_MODEL_FILES.has(originalFileName) && originalFile) {
        return originalFile;
    }

    const preferred = modelData.preferred || 'original';
    const fallbackOrder = Array.isArray(modelData.fallbackOrder) && modelData.fallbackOrder.length > 0
        ? modelData.fallbackOrder
        : (preferred === 'optimized' ? ['optimized', 'original'] : ['original', 'optimized']);

    const fileByType = {
        original: modelData.fileOriginal || modelData.file,
        optimized: modelData.fileOptimized
    };

    for (const candidate of fallbackOrder) {
        const file = fileByType[candidate];
        if (typeof file === 'string' && file.trim().length > 0) {
            return file;
        }
    }

    return modelData.file || modelData.fileOriginal || modelData.fileOptimized;
}

function resolveAnimationLoopMode(modelData) {
    const loopSetting = String(modelData?.animationLoop || 'repeat').toLowerCase();
    if (loopSetting === 'pingpong') return THREE.LoopPingPong;
    if (loopSetting === 'once') return THREE.LoopOnce;
    return THREE.LoopRepeat;
}

function resolveOutlineConfig(modelData) {
    const config = modelData?.outline;
    if (!config || config.enabled !== true) return null;

    const mode = String(config.mode || '').toLowerCase();
    if (mode !== 'invertedhull') return null;

    const thickness = Number(config.thickness);
    const hasValidThickness = Number.isFinite(thickness) && thickness > 0;

    return {
        mode: 'invertedHull',
        color: config.color || DEFAULT_OUTLINE_COLOR,
        thickness: hasValidThickness ? thickness : DEFAULT_OUTLINE_THICKNESS
    };
}

function createInvertedHullMaterial({
    color = DEFAULT_OUTLINE_COLOR,
    thickness = DEFAULT_OUTLINE_THICKNESS
} = {}) {
    return new THREE.ShaderMaterial({
        uniforms: {
            uThickness: { value: thickness },
            uColor: { value: new THREE.Color(color) }
        },
        vertexShader: /* glsl */`
            uniform float uThickness;
            void main() {
                vec3 newPosition = position + normal * uThickness;
                gl_Position = projectionMatrix * modelViewMatrix * vec4(newPosition, 1.0);
            }
        `,
        fragmentShader: /* glsl */`
            uniform vec3 uColor;
            void main() {
                gl_FragColor = vec4(uColor, 1.0);
            }
        `,
        side: THREE.BackSide,
        depthTest: true,
        depthWrite: false,
        transparent: false
    });
}

function applyInvertedHullOutline(modelRoot, outlineConfig) {
    if (!modelRoot || !outlineConfig) return [];

    const createdOutlines = [];
    const outlineMaterial = createInvertedHullMaterial(outlineConfig);

    modelRoot.traverse((child) => {
        if (!child.isMesh || child.userData?.isInvertedHullOutline) return;
        if (!child.geometry || !child.geometry.attributes?.normal) return;
        if (child.isSkinnedMesh) return;

        const outlineMesh = new THREE.Mesh(child.geometry, outlineMaterial);
        outlineMesh.name = `${child.name || child.uuid}-outline`;
        outlineMesh.userData.isInvertedHullOutline = true;
        outlineMesh.castShadow = false;
        outlineMesh.receiveShadow = false;
        outlineMesh.renderOrder = (child.renderOrder || 0) + 1;
        outlineMesh.frustumCulled = child.frustumCulled;

        child.add(outlineMesh);
        createdOutlines.push(outlineMesh);
    });

    return createdOutlines;
}

function ensureOfficeMonitorVideoMaterial() {
    if (officeMonitorVideoMaterial) {
        return officeMonitorVideoMaterial;
    }

    officeMonitorVideoElement = document.createElement('video');
    officeMonitorVideoElement.src = OFFICE_VIDEO_PATH;
    officeMonitorVideoElement.crossOrigin = 'anonymous';
    officeMonitorVideoElement.loop = true;
    officeMonitorVideoElement.muted = true;
    officeMonitorVideoElement.playsInline = true;
    officeMonitorVideoElement.preload = 'auto';

    officeMonitorVideoTexture = new THREE.VideoTexture(officeMonitorVideoElement);
    officeMonitorVideoTexture.minFilter = THREE.LinearFilter;
    officeMonitorVideoTexture.magFilter = THREE.LinearFilter;
    officeMonitorVideoTexture.generateMipmaps = false;
    if ('colorSpace' in officeMonitorVideoTexture) {
        officeMonitorVideoTexture.colorSpace = THREE.SRGBColorSpace;
    } else {
        officeMonitorVideoTexture.encoding = THREE.sRGBEncoding;
    }

    officeMonitorVideoMaterial = new THREE.MeshBasicMaterial({
        map: officeMonitorVideoTexture
    });
    return officeMonitorVideoMaterial;
}

function applyOfficeMonitorVideoMaterial(modelRoot, modelData) {
    if (!modelRoot || modelData?.id !== OFFICE_VIDEO_MODEL_ID) return;

    const videoMaterial = ensureOfficeMonitorVideoMaterial();
    let replacedCount = 0;

    modelRoot.traverse((child) => {
        if (!child.isMesh || !child.material) return;

        if (Array.isArray(child.material)) {
            const nextMaterials = child.material.map((mat) => {
                if (mat && mat.name === OFFICE_VIDEO_MATERIAL_NAME) {
                    replacedCount++;
                    return videoMaterial;
                }
                return mat;
            });
            child.material = nextMaterials;
        } else if (child.material.name === OFFICE_VIDEO_MATERIAL_NAME) {
            child.material = videoMaterial;
            replacedCount++;
        }
    });

    if (replacedCount > 0 && officeMonitorVideoElement) {
        officeMonitorVideoElement.play().catch((error) => {
            console.warn('Video autoplay blocked for office monitor material:', error);
        });
    }
}

function ensureSteamTexture() {
    if (steamTexture) return steamTexture;
    steamTexture = new THREE.TextureLoader().load(STEAM_TEXTURE_PATH);
    steamTexture.wrapS = THREE.ClampToEdgeWrapping;
    steamTexture.wrapT = THREE.ClampToEdgeWrapping;
    steamTexture.minFilter = THREE.LinearFilter;
    steamTexture.magFilter = THREE.LinearFilter;
    if ('colorSpace' in steamTexture) {
        steamTexture.colorSpace = THREE.SRGBColorSpace;
    } else {
        steamTexture.encoding = THREE.sRGBEncoding;
    }
    return steamTexture;
}

function resetSteamParticle(sprite, randomizeAge = false) {
    const life = 1.8 + Math.random() * 1.8;
    const angle = Math.random() * Math.PI * 2;
    const radius = Math.random() * 0.03;
    const age = randomizeAge ? Math.random() * life : 0;
    sprite.userData.steam = {
        life,
        age,
        phase: Math.random() * Math.PI * 2,
        speedY: 0.03 + Math.random() * 0.05,
        drift: 0.01 + Math.random() * 0.02
    };
    sprite.position.set(Math.cos(angle) * radius, 0.0, Math.sin(angle) * radius);
    sprite.scale.setScalar(0.03);
    sprite.material.opacity = 0;
}

function createSteamSystem(emitter) {
    const texture = ensureSteamTexture();
    const group = new THREE.Group();
    group.name = 'steam-particles';
    emitter.add(group);

    const particles = [];
    const particleCount = 24;
    for (let i = 0; i < particleCount; i++) {
        const material = new THREE.SpriteMaterial({
            map: texture,
            color: 0xffffff,
            transparent: true,
            opacity: 0,
            depthWrite: false,
            depthTest: true
        });
        const sprite = new THREE.Sprite(material);
        sprite.renderOrder = 10;
        resetSteamParticle(sprite, true);
        group.add(sprite);
        particles.push(sprite);
    }

    return {
        emitter,
        group,
        particles
    };
}

function setupModelSteamEffect(modelRoot, modelData) {
    if (modelData?.id !== STEAM_TARGET_MODEL_ID) return null;
    const emitter = modelRoot.getObjectByName(STEAM_EMITTER_NAME);
    if (!emitter) {
        console.warn(`Steam emitter "${STEAM_EMITTER_NAME}" not found in ${modelData?.name || modelData?.id}`);
        return null;
    }

    return createSteamSystem(emitter);
}

function updateSteamSystem(steamSystem, deltaSeconds) {
    if (!steamSystem || !Array.isArray(steamSystem.particles)) return;
    const dt = Math.min(Math.max(deltaSeconds, 0), 0.05);

    steamSystem.particles.forEach((sprite) => {
        const state = sprite.userData.steam;
        if (!state) return;

        state.age += dt;
        if (state.age >= state.life) {
            resetSteamParticle(sprite, false);
            return;
        }

        const t = state.age / state.life;
        const swayX = Math.sin((state.phase + state.age) * 2.1) * state.drift;
        const swayZ = Math.cos((state.phase + state.age) * 1.7) * state.drift;
        sprite.position.x += swayX * dt;
        sprite.position.z += swayZ * dt;
        sprite.position.y += state.speedY * dt;

        const scale = 0.03 + t * 0.09;
        sprite.scale.set(scale, scale, 1);

        const fadeIn = Math.min(t / 0.22, 1);
        const fadeOut = Math.min((1 - t) / 0.55, 1);
        sprite.material.opacity = Math.max(0, Math.min(fadeIn * fadeOut * 0.42, 0.42));
    });
}

// Select a model
function selectModel(modelId) {
    const model = loadedModels.get(modelId);
    if (!model) return;
    
    // Hide all other models
    loadedModels.forEach((loadedModel, id) => {
        if (id !== modelId) {
            loadedModel.scene.visible = false;
            loadedModel.visible = false;
        }
    });
    
    // Show selected model
    model.scene.visible = true;
    model.visible = true;
    
    // Update UI selection
    document.querySelectorAll('.model-item').forEach(item => {
        item.classList.remove('active');
    });
    document.querySelector(`[data-model-id="${modelId}"]`).classList.add('active');
    
    // Update selected model
    selectedModel = model;

    // Apply per-model lighting defaults (environment, tone mapping, exposure).
    applyModelLightingDefaults(model.config).catch((error) => {
        console.error(`❌ Failed to apply lighting defaults for ${model.name}:`, error);
    });
    
    // Focus camera on model
    focusOnModel(model);
    
    // Update model info panel
    updateModelInfoPanel(model);
    
    // Update animation controls
    updateAnimationControls(model);
    
    console.log(`🎯 Selected model: ${model.name}`);
    if (primaryShadowLight && selectedModel) {
        try { fitDirLightToModel(primaryShadowLight, selectedModel); } catch (e) {}
    }
}

// Focus camera on model
function focusOnModel(model) {
    const box = new THREE.Box3().setFromObject(model.scene);
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());
    
    const maxDim = Math.max(size.x, size.y, size.z);
    
    // Calculate appropriate camera distance, ensuring it's within your preferred range (1.5 to 10)
    let cameraDistance = maxDim * 2.5; // More conservative multiplier
    cameraDistance = Math.max(2.5, Math.min(8, cameraDistance)); // Clamp between 2.5 and 8
    
    // Position camera at a good viewing angle
    const targetPosition = center.clone();
    targetPosition.x += cameraDistance * 0.7;
    targetPosition.y += cameraDistance * 0.5;
    targetPosition.z += cameraDistance * 0.7;
    
    animateCamera(targetPosition, center);
}

// Animate camera movement
function animateCamera(targetPosition, targetLookAt) {
    const startPosition = camera.position.clone();
    const startTarget = controls.target.clone();
    
    let progress = 0;
    const duration = 1500; // ms
    const startTime = Date.now();
    
    function animate() {
        const elapsed = Date.now() - startTime;
        progress = Math.min(elapsed / duration, 1);
        
        // Smooth easing
        const eased = 1 - Math.pow(1 - progress, 3);
        
        camera.position.lerpVectors(startPosition, targetPosition, eased);
        controls.target.lerpVectors(startTarget, targetLookAt, eased);
        controls.update();
        
        if (progress < 1) {
            requestAnimationFrame(animate);
        }
    }
    
    animate();
}

// Update model performance panel
function updateModelInfoPanel(model) {
    document.getElementById('model-performance-title').textContent = model.name;
    document.getElementById('object-count').textContent = model.objects;
    document.getElementById('vertex-count').textContent = model.vertices.toLocaleString();
    document.getElementById('face-count').textContent = model.faces.toLocaleString();
    document.getElementById('triangle-count').textContent = model.triangles.toLocaleString();
    document.getElementById('file-size').textContent = model.fileSizeLabel;
}

// Update animation controls
function updateAnimationControls(model) {
    const clipsList = document.getElementById('animation-clips-list');
    
    // Clear existing clips
    clipsList.innerHTML = '';
    
    if (model.animations && model.animations.length > 0) {
        // Display each animation clip
        model.animations.forEach((anim, index) => {
            const clipItem = document.createElement('div');
            clipItem.className = 'clip-item';
            clipItem.dataset.clipIndex = index;
            
            clipItem.innerHTML = `
                <span class="clip-name">${anim.name}</span>
                <span class="clip-duration">${anim.duration.toFixed(1)}s</span>
            `;
            
            // Add click handler to play specific animation
            clipItem.addEventListener('click', () => {
                // Remove active class from all clips
                document.querySelectorAll('.clip-item').forEach(item => {
                    item.classList.remove('active');
                });
                
                // Add active class to clicked clip
                clipItem.classList.add('active');
                
                // Play the selected animation
                playAnimation(model.id, index);
            });
            
            clipsList.appendChild(clipItem);
        });
    } else {
        // Show no animations message
        const noClipsMessage = document.createElement('div');
        noClipsMessage.className = 'no-clips-message';
        noClipsMessage.textContent = 'No animations available';
        clipsList.appendChild(noClipsMessage);
    }
}

// Setup UI event handlers
function setupUI() {
    const mobileMediaQuery = window.matchMedia('(max-width: 768px)');
    const controlsPanel = document.getElementById('professional-controls');
    const controlsToggleBtn = document.getElementById('toggle-controls');
    const galleryPanel = document.getElementById('model-gallery-sidebar');
    const galleryToggleBtn = document.getElementById('toggle-gallery');

    // Professional controls toggle
    controlsToggleBtn.addEventListener('click', () => {
        controlsPanel.classList.toggle('collapsed');
        const isCollapsed = controlsPanel.classList.contains('collapsed');
        controlsToggleBtn.textContent = isCollapsed ? '◀' : '▶';
        controlsToggleBtn.setAttribute('aria-expanded', String(!isCollapsed));
    });

    function applyGalleryToggleState(isCollapsed, persist = true) {
        galleryPanel.classList.toggle('collapsed', isCollapsed);
        galleryToggleBtn.textContent = isCollapsed ? '▲' : '▼';
        galleryToggleBtn.setAttribute('aria-expanded', String(!isCollapsed));
        if (persist) {
            sessionStorage.setItem('galleryCollapsed', isCollapsed ? '1' : '0');
        }
    }

    const initialGalleryCollapsed = sessionStorage.getItem('galleryCollapsed') === '1';
    applyGalleryToggleState(mobileMediaQuery.matches && initialGalleryCollapsed);

    galleryToggleBtn.addEventListener('click', () => {
        if (!mobileMediaQuery.matches) return;
        const isCollapsed = !galleryPanel.classList.contains('collapsed');
        applyGalleryToggleState(isCollapsed);
    });

    const syncResponsivePanels = () => {
        if (!mobileMediaQuery.matches) {
            applyGalleryToggleState(false, false);
        } else {
            const storedCollapsed = sessionStorage.getItem('galleryCollapsed') === '1';
            applyGalleryToggleState(storedCollapsed, false);
        }
    };

    if (mobileMediaQuery.addEventListener) {
        mobileMediaQuery.addEventListener('change', syncResponsivePanels);
    } else {
        mobileMediaQuery.addListener(syncResponsivePanels);
    }
    
    // Display controls
document.getElementById('show-background').addEventListener('change', (e) => {
        const show = e.target.checked;
        if (show) {
            // Restore the last environment background (color or HDR), not the default
            if (lastEnvironmentBackground) {
                scene.background = lastEnvironmentBackground;
            } else {
                // Fallback: keep hidden until environment is set
                scene.background = null;
            }
        } else {
            // Hide background but keep environment lighting (scene.environment)
            scene.background = null;
        }
    });
    
    document.getElementById('show-wireframe').addEventListener('change', (e) => {
        scene.traverse((child) => {
            if (child.userData?.isInvertedHullOutline) return;
            if (child.isMesh && child.material) {
                if (Array.isArray(child.material)) {
                    child.material.forEach(mat => mat.wireframe = e.target.checked);
                } else {
                    child.material.wireframe = e.target.checked;
                }
            }
        });
    });

    document.getElementById('show-shadows').addEventListener('change', (e) => {
        const enabled = e.target.checked;
        renderer.shadowMap.enabled = enabled;
        scene.traverse((child) => {
            if (child.userData?.isInvertedHullOutline) return;
            if (child.isMesh) {
                child.castShadow = enabled;
                child.receiveShadow = enabled;
            }
            if (child.isAmbientLight) {
                child.castShadow = false;
            } else if (child.isLight) {
                child.castShadow = enabled;
            }
        });
    });
    
    document.getElementById('show-grid').addEventListener('change', (e) => {
        let gridHelper = scene.getObjectByName('grid-helper');
        let groundPlane = scene.getObjectByName('ground-plane');
        
        if (e.target.checked) {
            // Show grid and hide ground plane
            if (!gridHelper) {
                gridHelper = new THREE.GridHelper(20, 20, 0x444444, 0x222222);
                gridHelper.name = 'grid-helper';
                scene.add(gridHelper);
            }
            if (groundPlane) {
                groundPlane.visible = false;
            }
        } else {
            // Hide grid and show ground plane
            if (gridHelper) {
                scene.remove(gridHelper);
            }
            if (groundPlane) {
                groundPlane.visible = true;
            }
        }
    });
    
    // Environment controls
    const environmentSelect = document.getElementById('environment-select');
    environmentSelect.addEventListener('change', (e) => {
        changeEnvironment(e.target.value);
    });
    const defaultEnvironment = modelConfig?.settings?.defaultEnvironment ?? 'neutral';
    environmentSelect.value = defaultEnvironment;
    changeEnvironment(defaultEnvironment);
    
    // Tone mapping
    document.getElementById('tone-mapping').addEventListener('change', (e) => {
        setToneMapping(e.target.value);
        console.log(`🎨 Tone mapping changed to: ${e.target.value}`);
    });
    setToneMapping(document.getElementById('tone-mapping').value);
    
    // Exposure control
    const exposureSlider = document.getElementById('exposure-slider');
    exposureSlider.addEventListener('input', (e) => {
        const value = parseFloat(e.target.value);
        setExposure(value);
    });
    setExposure(DEFAULT_EXPOSURE);
    
    // Animation controls (right panel only)
    document.getElementById('play-btn').addEventListener('click', () => {
        if (selectedModel) {
            playAnimation(selectedModel.id);
        }
    });
    
    document.getElementById('pause-btn').addEventListener('click', () => {
        if (selectedModel) {
            pauseAnimation(selectedModel.id);
        }
    });
    
    document.getElementById('stop-btn').addEventListener('click', () => {
        if (selectedModel) {
            stopAnimation(selectedModel.id);
        }
    });
    
    // Animation speed control
    const speedSlider = document.getElementById('animation-speed');
    const speedValue = document.getElementById('speed-value');
    speedSlider.addEventListener('input', (e) => {
        const value = parseFloat(e.target.value);
        speedValue.textContent = value.toFixed(1);
        if (selectedModel && selectedModel.mixer) {
            selectedModel.mixer.timeScale = value;
        }
    });
    
    // Auto rotate control
    document.getElementById('auto-rotate').addEventListener('change', (e) => {
        controls.autoRotate = e.target.checked;
        console.log(`🔄 Auto rotate ${e.target.checked ? 'enabled' : 'disabled'}`);
    });

    // Sync checkbox and controls on load: UI takes precedence
    const autoRotateCheckbox = document.getElementById('auto-rotate');
    if (autoRotateCheckbox) {
        if (autoRotateCheckbox.checked !== controls.autoRotate) {
            controls.autoRotate = autoRotateCheckbox.checked;
        }
    }

    const shadowsCheckbox = document.getElementById('show-shadows');
    if (shadowsCheckbox) {
        const defaultShadows = modelConfig?.settings?.enableShadows ?? true;
        shadowsCheckbox.checked = defaultShadows;
        shadowsCheckbox.dispatchEvent(new Event('change'));
    }
}

// Change environment lighting
async function changeEnvironment(envType) {
    // Remove existing lights
    const lightsToRemove = [];
    scene.traverse((child) => {
        if (child.isLight) {
            lightsToRemove.push(child);
        }
    });
    lightsToRemove.forEach(light => scene.remove(light));

    // Clear existing environment map
    scene.environment = null;
    scene.fog = null;

    // Track current environment type
    currentEnvironmentType = envType;
    setExposure(DEFAULT_EXPOSURE);

    switch (envType) {
        case 'studio':
            setupStudioLighting();
            {
                const bg = new THREE.Color(0x2a2a3e);
                lastEnvironmentBackground = bg;
                const showBg = document.getElementById('show-background')?.checked ?? true;
                scene.background = showBg ? bg : null;
                scene.fog = new THREE.Fog(0x2a2a3e, 8, 25);
            }
            break;
        case 'outdoor':
            setupOutdoorLighting();
            {
                const bg = new THREE.Color(0x7680ad);
                lastEnvironmentBackground = bg;
                const showBg = document.getElementById('show-background')?.checked ?? true;
                scene.background = showBg ? bg : null;
                scene.fog = new THREE.Fog(0x5c6693, 8, 25);
            }
            break;
        case 'dark':
            setupDarkLighting();
            {
                const bg = new THREE.Color(0x0a0a0a);
                lastEnvironmentBackground = bg;
                const showBg = document.getElementById('show-background')?.checked ?? true;
                scene.background = showBg ? bg : null;
                scene.fog = new THREE.Fog(0x1a1a1a, 6, 20);
            }
            break;
        case 'neutral':
            setupNeutralLighting();
            {
                const bg = new THREE.Color(0x404040);
                lastEnvironmentBackground = bg;
                const showBg = document.getElementById('show-background')?.checked ?? true;
                scene.background = showBg ? bg : null;
                scene.fog = new THREE.Fog(0x666666, 8, 25);
            }
            break;
        case 'nature':
            await loadHDREnvironment('./textures/sunset.hdr', 'Sunset (HDR)');
            break;
    }
    
    console.log(`🌍 Environment changed to: ${envType}`);
}

// Load HDR environment
async function loadHDREnvironment(hdrPath, environmentName, applyWarmFilter = false) {
    try {
        showLoadingProgress(0, `Loading ${environmentName} HDR...`);
        
        const hdrLoader = new HDRLoader();
        
        const hdrTexture = await new Promise((resolve, reject) => {
            hdrLoader.load(hdrPath, resolve, undefined, reject);
        });
        
        // Apply blur to reduce pixelation from low-res HDR
        hdrTexture.mapping = THREE.EquirectangularReflectionMapping;
        
        // Create a blurred version for background
        const pmremGenerator = new THREE.PMREMGenerator(renderer);
        const envMap = pmremGenerator.fromEquirectangular(hdrTexture).texture;
        
        // Apply environment to scene (respect background toggle)
        scene.environment = envMap;
        lastEnvironmentBackground = envMap;
        const showBg = document.getElementById('show-background')?.checked ?? true;
        scene.background = showBg ? envMap : null;
        
        // Add subtle ambient lighting for HDR environments
        const ambientLight = new THREE.AmbientLight(0xffffff, 0.1);
        ambientLight.castShadow = false;
        scene.add(ambientLight);
        
        // Keep exposure consistent across all lighting scenarios.
        setExposure(DEFAULT_EXPOSURE);
        
        // Clean up
        hdrTexture.dispose();
        pmremGenerator.dispose();
        
        hideLoadingProgress();
        console.log(`✅ HDR environment loaded: ${environmentName}`);
        
    } catch (error) {
        console.error(`❌ Failed to load HDR environment: ${environmentName}`, error);
        hideLoadingProgress();
        
        // Fallback to neutral lighting
        setupNeutralLighting();
        scene.background = new THREE.Color(0x404040);
        scene.fog = new THREE.Fog(0x666666, 8, 25);
    }
}

function setupStudioLighting() {
    const ambientLight = new THREE.AmbientLight(0xaab3cb, 1.1);
    ambientLight.castShadow = false;
    scene.add(ambientLight);

    const keyLight = new THREE.DirectionalLight(0xffffff, 1.8);
    keyLight.position.set(5, 10, 5);
    configureShadowDirectional(keyLight, { mapSize: 4096, near: 0.1, far: 40, frustum: 7, bias: -0.0005, normalBias: 0.02 });
    scene.add(keyLight);
    if (!keyLight.target.parent) scene.add(keyLight.target);
    primaryShadowLight = keyLight;

    const fillLight = new THREE.DirectionalLight(0x4444ff, 0.6);
    fillLight.position.set(-5, 5, -5);
    scene.add(fillLight);

    const rimLight = new THREE.DirectionalLight(0xffffff, 0.9);
    rimLight.position.set(-5, 10, -5);
    scene.add(rimLight);
}

function setupOutdoorLighting() {
    const sunLight = new THREE.DirectionalLight(0xfff8dc, 2.0);
    sunLight.position.set(10, 20, 5);
    configureShadowDirectional(sunLight, { mapSize: 4096, near: 1, far: 80, frustum: 12, bias: -0.0005, normalBias: 0.03 });
    scene.add(sunLight);
    if (!sunLight.target.parent) scene.add(sunLight.target);
    primaryShadowLight = sunLight;

    const skyLight = new THREE.AmbientLight(0xf4f4f0, 2);
    skyLight.castShadow = false;
    scene.add(skyLight);
}

function setupDarkLighting() {
    const spotLight = new THREE.SpotLight(0xffffff, 2);
    spotLight.position.set(0, 10, 0);
    spotLight.angle = Math.PI / 4;
    spotLight.penumbra = 1.1;
    configureShadowSpot(spotLight, { mapSize: 2048, near: 1, far: 40, bias: -0.0001, normalBias: 0.03 });
    scene.add(spotLight);
    primaryShadowLight = spotLight;

    const ambientLight = new THREE.AmbientLight(0x202020, 0.1);
    ambientLight.castShadow = false;
    scene.add(ambientLight);
}

function setupNeutralLighting() {
    const light1 = new THREE.DirectionalLight(0xffffff, 1.5);
    light1.position.set(5, 5, 5);
    configureShadowDirectional(light1, { mapSize: 4096, near: 0.1, far: 40, frustum: 7, bias: -0.0005, normalBias: 0.02 });
    scene.add(light1);
    if (!light1.target.parent) scene.add(light1.target);
    primaryShadowLight = light1;

    const light2 = new THREE.DirectionalLight(0xffffff, 1.4);
    light2.position.set(-5, 5, -5);
    light2.castShadow = false;
    scene.add(light2);

    const ambientLight = new THREE.AmbientLight(0x4790bd, 0.6);
    ambientLight.castShadow = false;
    scene.add(ambientLight);
}

// Animation functions
function playAnimation(modelId, animationIndex = 0) {
    const model = loadedModels.get(modelId);
    if (!model || !model.mixer || !model.animations.length) return;
    
    const animation = model.animations[animationIndex];
    if (animation) {
        animation.action.reset().play();
        console.log(`▶ Playing animation: ${animation.name}`);
    }
}

function pauseAnimation(modelId) {
    const model = loadedModels.get(modelId);
    if (!model || !model.mixer) return;
    
    model.animations.forEach(anim => {
        anim.action.paused = true;
    });
}

function stopAnimation(modelId) {
    const model = loadedModels.get(modelId);
    if (!model || !model.mixer) return;
    
    model.animations.forEach(anim => {
        anim.action.stop();
    });
}

// Loading progress functions
function showLoadingProgress(percent, filename) {
    const loadingProgress = document.getElementById('loading-progress');
    const progressFill = document.querySelector('.progress-fill');
    const progressText = document.querySelector('.progress-text');
    
    loadingProgress.classList.remove('hidden');
    progressFill.style.width = `${percent}%`;
    progressText.textContent = `Loading ${filename}... ${Math.round(percent)}%`;
}

function hideLoadingProgress() {
    const loadingProgress = document.getElementById('loading-progress');
    loadingProgress.classList.add('hidden');
}

// Performance monitoring
function updatePerformanceStats() {
    frameCount++;
    const currentTime = performance.now();
    
    if (currentTime >= lastTime + 1000) {
        stats.fps = Math.round((frameCount * 1000) / (currentTime - lastTime));
        frameCount = 0;
        lastTime = currentTime;
        
        // Update FPS only (other stats are handled by model selection)
        document.getElementById('fps-counter').textContent = stats.fps;
    }
}

// WebXR setup
async function initWebXR() {
    const xrButton = document.getElementById('xr-button');
    
    if (!navigator.xr) {
        console.log('WebXR not supported');
        return;
    }

    try {
        const supported = await navigator.xr.isSessionSupported('immersive-vr');
        if (supported) {
            xrButton.classList.remove('hidden');
            xrButton.onclick = async () => {
                try {
                    const session = await navigator.xr.requestSession('immersive-vr', {
                        requiredFeatures: ['local-floor'],
                        optionalFeatures: ['local', 'bounded-floor']
                    });
                    
                    // Set up VR session event handlers
                    session.addEventListener('end', onXRSessionEnd);
                    
                    // Set up the session with custom positioning
                    await setupVRSession(session);
                    
                    console.log('🥽 WebXR session started');
                } catch (error) {
                    console.error('Failed to start WebXR session:', error);
                }
            };
        }
    } catch (error) {
        console.error('Error checking WebXR support:', error);
    }
}

// Setup VR session with proper positioning
async function setupVRSession(session) {
    // Calculate desired VR position before setting session
    const vrPosition = calculateVRPosition();
    
    // Create a reference space offset to position the user
    const referenceSpace = await session.requestReferenceSpace('local-floor');
    
    // Create transform matrix for the desired position
    const transform = new XRRigidTransform(
        { x: vrPosition.x, y: vrPosition.y, z: vrPosition.z },
        { x: 0, y: 0, z: 0, w: 1 } // No rotation
    );
    
    // Get offset reference space
    const offsetReferenceSpace = referenceSpace.getOffsetReferenceSpace(transform);
    
    // Set the session with the offset reference space
    await renderer.xr.setSession(session);
    renderer.xr.setReferenceSpace(offsetReferenceSpace);
    
    console.log(`🥽 VR positioned at: ${vrPosition.x.toFixed(2)}, ${vrPosition.y.toFixed(2)}, ${vrPosition.z.toFixed(2)}`);
}

// Apply real-world scaling to models
function applyRealWorldScale(model, modelData, currentSize) {
    // Check if real-world scaling is enabled globally and for this model
    const vrSettings = modelConfig?.settings?.vrSettings;
    const realWorldScale = modelData?.realWorldScale;
    
    if (!vrSettings?.useRealWorldScale || !realWorldScale?.enabled) {
        // Fallback to old scaling logic if real-world scaling is disabled
        const maxDimension = Math.max(currentSize.x, currentSize.y, currentSize.z);
        if (maxDimension > 10) {
            const scale = 5 / maxDimension;
            model.scale.setScalar(scale);
            console.log(`📏 Applied fallback scaling: ${scale.toFixed(2)}x for ${modelData.name}`);
        }
        return;
    }
    
    // Calculate real-world scale
    const currentMaxDimension = Math.max(currentSize.x, currentSize.y, currentSize.z);
    const targetSize = realWorldScale.targetSize;
    const realWorldScaleFactor = targetSize / currentMaxDimension;
    
    // Apply the real-world scale
    model.scale.setScalar(realWorldScaleFactor);
    
    console.log(`🌍 Applied real-world scaling: ${realWorldScaleFactor.toFixed(2)}x for ${modelData.name} (target: ${targetSize}m)`);
    console.log(`📐 Original size: ${currentMaxDimension.toFixed(2)}m → Real-world size: ${targetSize}m`);
}

// Calculate VR position based on current model
function calculateVRPosition() {
    // Get the current selected model or default to helicopter
    let targetModel = selectedModel;
    if (!targetModel && loadedModels.has('helicopter')) {
        targetModel = loadedModels.get('helicopter');
    }
    
    if (targetModel) {
        // Calculate position in front of the model at floor level
        const modelBox = new THREE.Box3().setFromObject(targetModel.scene);
        const modelCenter = modelBox.getCenter(new THREE.Vector3());
        const modelSize = modelBox.getSize(new THREE.Vector3());
        
        // Position at floor level - let WebXR handle natural standing height
        const distanceFromModel = Math.max(modelSize.x, modelSize.z) * 0.8 + 1; // Close viewing distance
        
        // Adjust position to face helicopter straight-on (compensate for 45-degree offset)
        // Position at an angle that naturally faces the helicopter's front
        const angle = Math.PI / 4; // 45 degrees to compensate for helicopter orientation
        const offsetX = Math.sin(angle) * distanceFromModel;
        const offsetZ = Math.cos(angle) * distanceFromModel;
        
        return new THREE.Vector3(
            modelCenter.x - offsetX, // Adjusted X position
            0, // Floor level - WebXR will handle your natural standing height
            modelCenter.z - offsetZ // Adjusted Z position
        );
    } else {
        // Fallback position if no model is available - at floor level
        return new THREE.Vector3(0, 0, -3); // In front of origin, at floor level
    }
}


// Handle VR session end
function onXRSessionEnd() {
    console.log('🥽 WebXR session ended');
    // Camera will automatically return to previous position when exiting VR
}

// Animation loop
function animate() {
    timer.update();

    // Keep OrbitControls autoRotate in sync with the UI checkbox every frame
    const autoRotateCheckbox = document.getElementById('auto-rotate');
    if (autoRotateCheckbox && controls) {
        const shouldRotate = !!autoRotateCheckbox.checked;
        if (controls.autoRotate !== shouldRotate) {
            controls.autoRotate = shouldRotate;
        }
    }
    controls.update();
    
    // Update animation mixers
    const delta = timer.getDelta();
    animationMixers.forEach(mixer => {
        mixer.update(delta);
    });
    if (selectedModel?.steamSystem) {
        updateSteamSystem(selectedModel.steamSystem, delta);
    }
    
    // Update performance stats
    updatePerformanceStats();
    
    // Render
    renderer.render(scene, camera);
}

// Resize handler
window.addEventListener('resize', () => {
    if (!renderer.xr.isPresenting) {
        camera.aspect = window.innerWidth / window.innerHeight;
        camera.updateProjectionMatrix();
        renderer.setSize(window.innerWidth, window.innerHeight);
    }
});

// Initialize the application
init();
